import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ExternalLink,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { ImageIcon } from 'lucide-react'
import { MediaLibraryPickerDialog } from '../components/media/MediaLibraryPickerDialog'
import type { MediaDocument } from '../services/admin.media.service'
import { storage, ref, uploadBytesResumable, getDownloadURL } from '@/lib/firebase/storage'
import { useAdminPressKit } from '../hooks/useAdminPressKit'
import { useAdminPressKitStats } from '../hooks/useAdminPressKitStats'
import { useAdminPressKitAssets } from '../hooks/useAdminPressKitAssets'
import { useAdminPressKitReleases } from '../hooks/useAdminPressKitReleases'
import { useAdminPressKitCoverage } from '../hooks/useAdminPressKitCoverage'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type {
  PressKitStatWithId,
  PressKitAssetWithId,
  PressKitReleaseWithId,
  PressKitCoverageWithId,
  PressKitStatFormValues,
} from '@/features/press-kit/types'
import { DERIVED_STAT_LABELS } from '@/features/press-kit/types'
import type { DerivedStatKey, PressKitAssetCategory, PressKitAssetDocument } from '@/types'

// ─── Helpers ────────────────────────────────────────────────────────────────

const ASSET_CATEGORIES: PressKitAssetCategory[] = ['logo', 'color', 'font', 'photo', 'other']
const ASSET_CATEGORY_LABELS: Record<PressKitAssetCategory, string> = {
  logo: 'Logo',
  color: 'Color / Brand',
  font: 'Typography',
  photo: 'Photo',
  other: 'Other',
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(ts: unknown): string {
  if (!ts) return ''
  const d =
    typeof (ts as { toDate?: () => Date }).toDate === 'function'
      ? (ts as { toDate: () => Date }).toDate()
      : new Date(ts as string | number)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

// ─── Bio & Config Tab ───────────────────────────────────────────────────────

function BioTab() {
  const { config, loading, saving, error, saveConfig } = useAdminPressKit()
  const [bioTitle, setBioTitle] = useState('')
  const [bioContent, setBioContent] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [previewMode, setPreviewMode] = useState(false)

  useEffect(() => {
    if (config) {
      setBioTitle(config.bioTitle ?? '')
      setBioContent(config.bioContent ?? '')
      setContactName(config.contactName ?? '')
      setContactEmail(config.contactEmail ?? '')
      setIsPublished(config.isPublished ?? false)
    }
  }, [config])

  async function handleSave() {
    await saveConfig({ bioTitle, bioContent, contactName, contactEmail, isPublished })
  }

  if (loading) return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Switch
            id="pk-published"
            checked={isPublished}
            onCheckedChange={setIsPublished}
          />
          <Label htmlFor="pk-published">Published (visible to public)</Label>
        </div>
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="pk-contact-name">Media Contact Name</Label>
          <Input id="pk-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="e.g. Jane Smith" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pk-contact-email">Media Contact Email</Label>
          <Input id="pk-contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="press@example.com" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pk-bio-title">Bio Section Title</Label>
        <Input id="pk-bio-title" value={bioTitle} onChange={(e) => setBioTitle(e.target.value)} placeholder="e.g. About ODD" />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Bio Content (Markdown)</Label>
          <div className="flex rounded-md border text-xs overflow-hidden">
            <button type="button" onClick={() => setPreviewMode(false)} className={`px-3 py-1 transition-colors ${!previewMode ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>Write</button>
            <button type="button" onClick={() => setPreviewMode(true)} className={`px-3 py-1 border-l transition-colors ${previewMode ? 'bg-accent text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`}>Preview</button>
          </div>
        </div>
        {previewMode ? (
          <div className="min-h-[200px] rounded-md border bg-muted/30 px-3 py-2 prose prose-sm max-w-none text-muted-foreground">
            {bioContent.trim() ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{bioContent}</ReactMarkdown> : <p className="italic text-muted-foreground/50">Nothing to preview.</p>}
          </div>
        ) : (
          <Textarea value={bioContent} onChange={(e) => setBioContent(e.target.value)} rows={10} className="resize-y font-mono text-sm" placeholder="Markdown is supported — **bold**, _italic_, bullet lists, links, etc." />
        )}
      </div>
    </div>
  )
}

// ─── Stats Tab ──────────────────────────────────────────────────────────────

interface SortableStatRowProps {
  stat: PressKitStatWithId
  onEdit: (stat: PressKitStatWithId) => void
  onDelete: (id: string) => void
}

function SortableStatRow({ stat, onEdit, onDelete }: SortableStatRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stat.id })
  return (
    <TableRow ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}>
      <TableCell className="w-8 pr-0">
        <button type="button" className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground" {...attributes} {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{stat.label}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-xs">{stat.type}</Badge>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {stat.type === 'manual' ? stat.value : (stat.derivedKey ? DERIVED_STAT_LABELS[stat.derivedKey] : '—')}
      </TableCell>
      <TableCell>
        <Switch checked={stat.isVisible} onCheckedChange={async (v) => { await adminPressKitService.updateStat(stat.id, { isVisible: v } as Partial<PressKitStatFormValues>); onEdit({ ...stat, isVisible: v }) }} />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton variant="outline" size="sm" items={[
            { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => onEdit(stat) },
            { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => onDelete(stat.id), className: 'text-destructive hover:text-destructive' },
          ]} />
        </div>
      </TableCell>
    </TableRow>
  )
}

function StatsTab() {
  const { stats, loading, error, createStat, updateStat, updateStatOrder, deleteStat, refresh } = useAdminPressKitStats()
  const [localStats, setLocalStats] = useState<PressKitStatWithId[]>([])

  useEffect(() => { setLocalStats(stats) }, [stats])

  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<PressKitStatWithId | null>(null)
  const [statType, setStatType] = useState<'manual' | 'derived'>('manual')
  const [labelInput, setLabelInput] = useState('')
  const [valueInput, setValueInput] = useState('')
  const [derivedKey, setDerivedKey] = useState<DerivedStatKey>('films_published')
  const [isVisible, setIsVisible] = useState(true)
  const [formSaving, setFormSaving] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = localStats.findIndex((s) => s.id === active.id)
    const newIndex = localStats.findIndex((s) => s.id === over.id)
    const reordered = arrayMove(localStats, oldIndex, newIndex)
    setLocalStats(reordered)
    void Promise.all(reordered.map((s, i) => updateStatOrder(s.id, i)))
  }

  function openAdd() {
    setEditTarget(null)
    setStatType('manual')
    setLabelInput('')
    setValueInput('')
    setDerivedKey('films_published')
    setIsVisible(true)
    setFormOpen(true)
  }

  function openEdit(stat: PressKitStatWithId) {
    setEditTarget(stat)
    setStatType(stat.type)
    setLabelInput(stat.label)
    setValueInput(stat.value ?? '')
    setDerivedKey(stat.derivedKey ?? 'films_published')
    setIsVisible(stat.isVisible)
    setFormOpen(true)
  }

  async function handleFormSave() {
    if (!labelInput.trim()) return
    setFormSaving(true)
    try {
      const values: PressKitStatFormValues =
        statType === 'manual'
          ? { type: 'manual', label: labelInput.trim(), value: valueInput.trim(), isVisible }
          : { type: 'derived', label: labelInput.trim(), derivedKey, isVisible }

      if (editTarget) {
        await updateStat(editTarget.id, values as Partial<PressKitStatFormValues>)
      } else {
        await createStat(values)
      }
      setFormOpen(false)
      refresh()
    } finally {
      setFormSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAdd}><Plus className="h-4 w-4 mr-1" />Add Stat</Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Label</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value / Source</TableHead>
                  <TableHead>Visible</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <MultiButtonGroup>
                <SortableContext items={localStats.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  <TableBody>
                    {localStats.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No stats yet. Add one to get started.</TableCell></TableRow>
                    ) : (
                      localStats.map((stat) => (
                        <SortableStatRow key={stat.id} stat={stat} onEdit={openEdit} onDelete={(id) => { void deleteStat(id) }} />
                      ))
                    )}
                  </TableBody>
                </SortableContext>
              </MultiButtonGroup>
            </Table>
          </DndContext>
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editTarget ? 'Edit Stat' : 'Add Stat'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={statType} onValueChange={(v) => setStatType(v as 'manual' | 'derived')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual — enter a custom value</SelectItem>
                  <SelectItem value="derived">Derived — auto-counted from site data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stat-label">Label</Label>
              <Input id="stat-label" value={labelInput} onChange={(e) => setLabelInput(e.target.value)} placeholder="e.g. Films Produced" />
            </div>
            {statType === 'manual' ? (
              <div className="space-y-2">
                <Label htmlFor="stat-value">Value</Label>
                <Input id="stat-value" value={valueInput} onChange={(e) => setValueInput(e.target.value)} placeholder="e.g. Founded 2018" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Data Source</Label>
                <Select value={derivedKey} onValueChange={(v) => setDerivedKey(v as DerivedStatKey)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(DERIVED_STAT_LABELS) as DerivedStatKey[]).map((k) => (
                      <SelectItem key={k} value={k}>{DERIVED_STAT_LABELS[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch id="stat-visible" checked={isVisible} onCheckedChange={setIsVisible} />
              <Label htmlFor="stat-visible">Visible on press kit</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleFormSave()} disabled={!labelInput.trim() || (statType === 'manual' && !valueInput.trim()) || formSaving}>
              {formSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Assets Tab ─────────────────────────────────────────────────────────────

interface AssetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editTarget: PressKitAssetWithId | null
  existingTags: string[]
  onCreate: (asset: Omit<PressKitAssetDocument, 'id' | 'createdAt' | 'updatedAt'>) => void
  onUpdate: (id: string, patch: Partial<PressKitAssetDocument>) => void
}

const HEX_PATTERN = /^#?([0-9a-fA-F]{6})$/

function normalizeHex(input: string): string | null {
  const m = input.trim().match(HEX_PATTERN)
  return m ? `#${m[1]!.toUpperCase()}` : null
}

function AssetDialog({ open, onOpenChange, editTarget, existingTags, onCreate, onUpdate }: AssetDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [nameInput, setNameInput] = useState('')
  const [category, setCategory] = useState<PressKitAssetCategory>('logo')
  const [description, setDescription] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [hexInput, setHexInput] = useState('')
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)
  const [libraryItem, setLibraryItem] = useState<MediaDocument | null>(null)

  const isEdit = editTarget !== null
  const isColor = category === 'color'
  const isPhoto = category === 'photo'
  const normalizedHex = isColor ? normalizeHex(hexInput) : null

  useEffect(() => {
    if (open) {
      setFile(null)
      setLibraryItem(null)
      setProgress(0)
      setError(null)
      if (editTarget) {
        setNameInput(editTarget.name)
        setCategory(editTarget.category)
        setDescription(editTarget.description ?? '')
        setTagInput(editTarget.tag ?? '')
        setHexInput(editTarget.hexCode ?? '')
      } else {
        setNameInput('')
        setCategory('logo')
        setDescription('')
        setTagInput('')
        setHexInput('')
      }
    }
  }, [open, editTarget])

  function handleClose(v: boolean) {
    if (!uploading) onOpenChange(v)
  }

  async function handleSubmit() {
    if (!nameInput.trim()) return
    setError(null)

    // ── Color assets ──────────────────────────────────────────────────────
    if (isColor) {
      if (!normalizedHex) {
        setError(withErrorCode('Enter a valid 6-digit hex code (e.g. #FF00AA)', 'app/validation'))
        return
      }
      if (isEdit && editTarget) {
        onUpdate(editTarget.id, {
          name: nameInput.trim(),
          category,
          description: description.trim(),
          tag: tagInput.trim(),
          hexCode: normalizedHex,
          // clear file fields in case this was previously a file asset
          fileUrl: '',
          fileName: '',
          fileSize: 0,
          fileType: '',
        })
      } else {
        onCreate({
          name: nameInput.trim(),
          category,
          fileUrl: '',
          fileName: '',
          fileSize: 0,
          fileType: '',
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(tagInput.trim() ? { tag: tagInput.trim() } : {}),
          hexCode: normalizedHex,
          order: 0,
          isVisible: true,
        })
      }
      handleClose(false)
      return
    }

    // ── Library selection (photo category) ──────────────────────────────
    if (libraryItem) {
      const payload = {
        name: nameInput.trim(),
        category,
        fileUrl: libraryItem.url,
        thumbnailUrl: libraryItem.thumbnailUrl ?? libraryItem.url,
        fileName: libraryItem.originalName || libraryItem.name,
        fileSize: libraryItem.size,
        fileType: libraryItem.mimeType,
      }
      if (isEdit && editTarget) {
        onUpdate(editTarget.id, {
          ...payload,
          description: description.trim(),
          tag: tagInput.trim(),
        })
      } else {
        onCreate({
          ...payload,
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(tagInput.trim() ? { tag: tagInput.trim() } : {}),
          order: 0,
          isVisible: true,
        })
      }
      handleClose(false)
      return
    }

    // ── File assets — edit metadata only (existing file, not a color->file switch) ──
    if (isEdit && editTarget && !file && editTarget.category !== 'color') {
      onUpdate(editTarget.id, {
        name: nameInput.trim(),
        category,
        description: description.trim(),
        tag: tagInput.trim(),
      })
      handleClose(false)
      return
    }

    // ── File upload (create, or edit + replace) ──────────────────────────
    if (!file) return
    setUploading(true)
    const storagePath = `press-kit-assets/${Date.now()}-${file.name}`
    const storageRef = ref(storage, storagePath)
    const task = uploadBytesResumable(storageRef, file)

    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', (s) => setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)), reject, resolve)
    })
      .then(async () => {
        const fileUrl = await getDownloadURL(storageRef)
        if (isEdit && editTarget) {
          onUpdate(editTarget.id, {
            name: nameInput.trim(),
            category,
            description: description.trim(),
            tag: tagInput.trim(),
            fileUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          })
        } else {
          onCreate({
            name: nameInput.trim(),
            category,
            fileUrl,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            ...(description.trim() ? { description: description.trim() } : {}),
            ...(tagInput.trim() ? { tag: tagInput.trim() } : {}),
            order: 0,
            isVisible: true,
          })
        }
        handleClose(false)
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Upload failed')))
      .finally(() => { setUploading(false); setProgress(0) })
  }

  const needsFileForEdit = isEdit && editTarget?.category === 'color' && !isColor
  const canSubmit =
    !!nameInput.trim() &&
    !uploading &&
    (isColor
      ? !!normalizedHex
      : libraryItem
        ? true
        : isEdit && !needsFileForEdit
          ? true
          : !!file)

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Asset' : 'Upload Asset'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="asset-name">Name</Label>
            <Input id="asset-name" value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="e.g. Primary Logo — Light" />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as PressKitAssetCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSET_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{ASSET_CATEGORY_LABELS[c]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="asset-desc">Description (optional)</Label>
            <Input id="asset-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. SVG format, white variant" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="asset-tag">Tag (optional)</Label>
            <Input
              id="asset-tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="e.g. Primary, Dark Mode"
              list="pk-asset-tags"
            />
            <datalist id="pk-asset-tags">
              {existingTags.map((t) => <option key={t} value={t} />)}
            </datalist>
            <p className="text-xs text-muted-foreground">Assets with the same tag are grouped together on the public press kit.</p>
          </div>
          {isColor ? (
            <div className="space-y-2">
              <Label htmlFor="asset-hex">Hex Code</Label>
              <div className="flex items-center gap-2">
                <div
                  className="h-10 w-10 shrink-0 rounded-md border"
                  style={{ backgroundColor: normalizedHex ?? 'transparent' }}
                  aria-hidden
                />
                <Input
                  id="asset-hex"
                  value={hexInput}
                  onChange={(e) => setHexInput(e.target.value)}
                  placeholder="#FF00AA"
                  className="font-mono"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>{isEdit ? 'Replace File (optional)' : 'File'}</Label>
              {isEdit && editTarget?.fileName && !file && !libraryItem && (
                <p className="text-xs text-muted-foreground truncate">Current: {editTarget.fileName}</p>
              )}
              {libraryItem && (
                <div className="flex items-center gap-2 rounded-md border p-2">
                  <img src={libraryItem.url} alt="" className="h-10 w-10 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{libraryItem.name}</p>
                    <p className="text-xs text-muted-foreground">From media library</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setLibraryItem(null)}>Clear</Button>
                </div>
              )}
              <input ref={fileInputRef} type="file" className="sr-only" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setLibraryItem(null) }} disabled={uploading} />
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="h-4 w-4 mr-2" />{file ? file.name : 'Upload File'}
                </Button>
                {isPhoto && (
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setLibraryOpen(true)} disabled={uploading}>
                    <ImageIcon className="h-4 w-4 mr-2" />Media Library
                  </Button>
                )}
              </div>
            </div>
          )}
          {uploading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground"><span>Uploading…</span><span>{progress}%</span></div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all duration-200" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {uploading ? 'Uploading…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <MediaLibraryPickerDialog
      open={libraryOpen}
      onClose={() => setLibraryOpen(false)}
      onSelect={() => {}}
      onSelectItem={(item) => { setLibraryItem(item); setFile(null) }}
    />
    </>
  )
}

function AssetsTab() {
  const { assets, loading, error, createAsset, updateAsset, deleteAsset } = useAdminPressKitAssets()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<PressKitAssetWithId | null>(null)

  function openAdd() {
    setEditTarget(null)
    setDialogOpen(true)
  }
  function openEdit(asset: PressKitAssetWithId) {
    setEditTarget(asset)
    setDialogOpen(true)
  }

  const grouped = ASSET_CATEGORIES.reduce<Record<string, PressKitAssetWithId[]>>((acc, cat) => {
    acc[cat] = assets.filter((a) => a.category === cat)
    return acc
  }, {})

  const existingTags = Array.from(
    new Set(assets.map((a) => a.tag?.trim()).filter((t): t is string => !!t)),
  ).sort()

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button onClick={openAdd}><Upload className="h-4 w-4 mr-1" />Add Asset</Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : assets.length === 0 ? (
        <div className="rounded-md border py-12 text-center text-sm text-muted-foreground">
          No assets yet. Add one to get started.
        </div>
      ) : (
        <div className="space-y-6">
          {ASSET_CATEGORIES.filter((c) => grouped[c]!.length > 0).map((cat) => (
            <div key={cat}>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {ASSET_CATEGORY_LABELS[cat]}
              </h3>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Tag</TableHead>
                      <TableHead>{cat === 'color' ? 'Hex' : 'File'}</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <MultiButtonGroup>
                    <TableBody>
                      {grouped[cat]!.map((asset) => (
                        <AssetRow
                          key={asset.id}
                          asset={asset}
                          onEdit={() => openEdit(asset)}
                          onDelete={() => { void deleteAsset(asset.id) }}
                        />
                      ))}
                    </TableBody>
                  </MultiButtonGroup>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}
      <AssetDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editTarget={editTarget}
        existingTags={existingTags}
        onCreate={(data) => { void createAsset(data) }}
        onUpdate={(id, patch) => { void updateAsset(id, patch) }}
      />
    </div>
  )
}

function AssetRow({ asset, onEdit, onDelete }: { asset: PressKitAssetWithId; onEdit: () => void; onDelete: () => void }) {
  const isColor = asset.category === 'color'
  const items = [
    { id: 'edit', icon: Pencil, label: 'Edit', onClick: onEdit },
    ...(!isColor
      ? [{
          id: 'open',
          icon: ExternalLink,
          label: 'Open',
          onClick: () => { window.open(asset.fileUrl, '_blank', 'noopener,noreferrer') },
        }]
      : []),
    { id: 'delete', icon: Trash2, label: 'Delete', onClick: onDelete, className: 'text-destructive hover:text-destructive' },
  ]
  return (
    <TableRow>
      <TableCell className="font-medium">{asset.name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {asset.tag ? <Badge variant="secondary" className="text-xs">{asset.tag}</Badge> : <span className="text-muted-foreground/60">—</span>}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground truncate max-w-xs">
        {isColor ? (
          <span className="inline-flex items-center gap-2">
            <span className="h-4 w-4 rounded border" style={{ backgroundColor: asset.hexCode ?? 'transparent' }} aria-hidden />
            <span className="font-mono">{asset.hexCode ?? '—'}</span>
          </span>
        ) : (
          asset.fileName
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{isColor ? '—' : formatFileSize(asset.fileSize)}</TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton variant="outline" size="sm" items={items} />
        </div>
      </TableCell>
    </TableRow>
  )
}

// ─── Releases Tab ────────────────────────────────────────────────────────────

function ReleasesTab() {
  const { releases, loading, error, createRelease, updateRelease, deleteRelease } = useAdminPressKitReleases()
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<PressKitReleaseWithId | null>(null)
  const [titleInput, setTitleInput] = useState('')
  const [summaryInput, setSummaryInput] = useState('')
  const [contentInput, setContentInput] = useState('')
  const [publishedAt, setPublishedAt] = useState(new Date().toISOString().slice(0, 10))
  const [isPublished, setIsPublished] = useState(true)
  const [previewMode, setPreviewMode] = useState(false)
  const [formSaving, setFormSaving] = useState(false)

  function openAdd() {
    setEditTarget(null)
    setTitleInput('')
    setSummaryInput('')
    setContentInput('')
    setPublishedAt(new Date().toISOString().slice(0, 10))
    setIsPublished(true)
    setPreviewMode(false)
    setFormOpen(true)
  }

  function openEdit(release: PressKitReleaseWithId) {
    setEditTarget(release)
    setTitleInput(release.title)
    setSummaryInput(release.summary)
    setContentInput(release.content)
    const d = typeof (release.publishedAt as unknown as { toDate?: () => Date }).toDate === 'function'
      ? (release.publishedAt as unknown as { toDate: () => Date }).toDate()
      : new Date(release.publishedAt as unknown as string)
    setPublishedAt(d.toISOString().slice(0, 10))
    setIsPublished(release.isPublished)
    setPreviewMode(false)
    setFormOpen(true)
  }

  async function handleFormSave() {
    if (!titleInput.trim() || !summaryInput.trim() || !contentInput.trim()) return
    setFormSaving(true)
    try {
      const values = { title: titleInput.trim(), summary: summaryInput.trim(), content: contentInput.trim(), publishedAt: new Date(publishedAt), isPublished }
      if (editTarget) {
        await updateRelease(editTarget.id, values)
      } else {
        await createRelease(values)
      }
      setFormOpen(false)
    } finally {
      setFormSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAdd}><Plus className="h-4 w-4 mr-1" />New Release</Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <MultiButtonGroup>
              <TableBody>
                {releases.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No press releases yet.</TableCell></TableRow>
                ) : (
                  releases.map((release) => (
                    <TableRow key={release.id}>
                      <TableCell className="font-medium">{release.title}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(release.publishedAt)}</TableCell>
                      <TableCell>
                        <Badge variant={release.isPublished ? 'default' : 'secondary'}>
                          {release.isPublished ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <MultiButton variant="outline" size="sm" items={[
                            { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => openEdit(release) },
                            { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => { void deleteRelease(release.id) }, className: 'text-destructive hover:text-destructive' },
                          ]} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </MultiButtonGroup>
          </Table>
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
          <DialogHeader><DialogTitle>{editTarget ? 'Edit Press Release' : 'New Press Release'}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            <div className="space-y-2">
              <Label htmlFor="rel-title">Title</Label>
              <Input id="rel-title" value={titleInput} onChange={(e) => setTitleInput(e.target.value)} placeholder="Release title" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="rel-date">Publication Date</Label>
                <Input id="rel-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} />
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <Switch id="rel-published" checked={isPublished} onCheckedChange={setIsPublished} />
                <Label htmlFor="rel-published">Published</Label>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rel-summary">Summary (shown in list view)</Label>
              <Textarea id="rel-summary" value={summaryInput} onChange={(e) => setSummaryInput(e.target.value)} rows={2} placeholder="Short excerpt…" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Full Content (Markdown)</Label>
                <div className="flex rounded-md border text-xs overflow-hidden">
                  <button type="button" onClick={() => setPreviewMode(false)} className={`px-3 py-1 transition-colors ${!previewMode ? 'bg-accent font-medium' : 'text-muted-foreground hover:text-foreground'}`}>Write</button>
                  <button type="button" onClick={() => setPreviewMode(true)} className={`px-3 py-1 border-l transition-colors ${previewMode ? 'bg-accent font-medium' : 'text-muted-foreground hover:text-foreground'}`}>Preview</button>
                </div>
              </div>
              {previewMode ? (
                <div className="min-h-[200px] rounded-md border bg-muted/30 px-3 py-2 prose prose-sm max-w-none text-muted-foreground">
                  {contentInput.trim() ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentInput}</ReactMarkdown> : <p className="italic text-muted-foreground/50">Nothing to preview.</p>}
                </div>
              ) : (
                <Textarea value={contentInput} onChange={(e) => setContentInput(e.target.value)} rows={12} className="resize-y font-mono text-sm" placeholder="Markdown is supported…" />
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleFormSave()} disabled={!titleInput.trim() || !summaryInput.trim() || !contentInput.trim() || formSaving}>
              {formSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Coverage Tab ────────────────────────────────────────────────────────────

interface CoverageThumbnailDialogProps {
  coverageId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (url: string) => void
}

function CoverageThumbnailDialog({ coverageId, open, onOpenChange, onSuccess }: CoverageThumbnailDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null
    setFile(f)
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(f ? URL.createObjectURL(f) : null)
  }

  function handleClose(v: boolean) {
    if (!uploading) {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setFile(null)
      setPreviewUrl(null)
      setProgress(0)
      setError(null)
      onOpenChange(v)
    }
  }

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    const storageRef = ref(storage, `press-kit-coverage/${coverageId}`)
    const task = uploadBytesResumable(storageRef, file)
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', (s) => setProgress(Math.round((s.bytesTransferred / s.totalBytes) * 100)), reject, resolve)
    })
      .then(async () => {
        const url = await getDownloadURL(storageRef)
        onSuccess(url)
        handleClose(false)
      })
      .catch((err: unknown) => setError(formatUserFacingError(err, 'Upload failed')))
      .finally(() => { setUploading(false); setProgress(0) })
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Upload Thumbnail</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {previewUrl ? (
            <div className="aspect-video rounded-md overflow-hidden bg-muted"><img src={previewUrl} alt="Preview" className="w-full h-full object-cover" /></div>
          ) : (
            <div className="aspect-video rounded-md border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-muted-foreground">
              <div className="text-center"><Upload className="mx-auto mb-1 h-6 w-6" /><p className="text-xs">No image selected</p></div>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" className="sr-only" onChange={handleFileChange} disabled={uploading} />
          <Button type="button" variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={uploading}>Choose Image</Button>
          {uploading && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground"><span>Uploading…</span><span>{progress}%</span></div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={() => void handleUpload()} disabled={!file || uploading}>{uploading ? 'Uploading…' : 'Upload'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CoverageTab() {
  const { coverage, loading, error, createCoverage, updateThumbnail, deleteCoverage } = useAdminPressKitCoverage()
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<PressKitCoverageWithId | null>(null)
  const [titleInput, setTitleInput] = useState('')
  const [publication, setPublication] = useState('')
  const [url, setUrl] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [coverageDate, setCoverageDate] = useState(new Date().toISOString().slice(0, 10))
  const [isVisible, setIsVisible] = useState(true)
  const [formSaving, setFormSaving] = useState(false)
  const [thumbnailTarget, setThumbnailTarget] = useState<PressKitCoverageWithId | null>(null)
  const [thumbnailOpen, setThumbnailOpen] = useState(false)

  function openAdd() {
    setEditTarget(null)
    setTitleInput('')
    setPublication('')
    setUrl('')
    setExcerpt('')
    setCoverageDate(new Date().toISOString().slice(0, 10))
    setIsVisible(true)
    setFormOpen(true)
  }

  async function handleFormSave() {
    if (!titleInput.trim() || !publication.trim() || !url.trim()) return
    setFormSaving(true)
    try {
      const values = { title: titleInput.trim(), publication: publication.trim(), url: url.trim(), excerpt: excerpt.trim(), coverageDate: new Date(coverageDate), isVisible }
      if (editTarget) {
        // update coverage via service directly since updateCoverage isn't exposed here
        await adminPressKitService.updateCoverage(editTarget.id, values)
      } else {
        await createCoverage(values)
      }
      setFormOpen(false)
    } finally {
      setFormSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={openAdd}><Plus className="h-4 w-4 mr-1" />Add Coverage</Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Publication</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Thumbnail</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <MultiButtonGroup>
              <TableBody>
                {coverage.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No media coverage yet.</TableCell></TableRow>
                ) : (
                  coverage.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium max-w-xs truncate">{item.title}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.publication}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(item.coverageDate)}</TableCell>
                      <TableCell>
                        {item.thumbnailUrl ? (
                          <img src={item.thumbnailUrl} alt="" className="h-8 w-14 object-cover rounded" />
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <MultiButton variant="outline" size="sm" items={[
                            { id: 'edit', icon: Pencil, label: 'Edit', onClick: () => {
                              setEditTarget(item)
                              setTitleInput(item.title)
                              setPublication(item.publication)
                              setUrl(item.url)
                              setExcerpt(item.excerpt)
                              const d = typeof (item.coverageDate as unknown as { toDate?: () => Date }).toDate === 'function'
                                ? (item.coverageDate as unknown as { toDate: () => Date }).toDate()
                                : new Date(item.coverageDate as unknown as string)
                              setCoverageDate(d.toISOString().slice(0, 10))
                              setIsVisible(item.isVisible)
                              setFormOpen(true)
                            }},
                            { id: 'thumbnail', icon: Upload, label: 'Thumbnail', onClick: () => { setThumbnailTarget(item); setThumbnailOpen(true) } },
                            { id: 'delete', icon: Trash2, label: 'Delete', onClick: () => { void deleteCoverage(item.id) }, className: 'text-destructive hover:text-destructive' },
                          ]} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </MultiButtonGroup>
          </Table>
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogTrigger className="hidden" />
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editTarget ? 'Edit Coverage' : 'Add Coverage'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cov-title">Article Title</Label>
              <Input id="cov-title" value={titleInput} onChange={(e) => setTitleInput(e.target.value)} placeholder="Article or feature title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cov-pub">Publication</Label>
              <Input id="cov-pub" value={publication} onChange={(e) => setPublication(e.target.value)} placeholder="e.g. The New York Times" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cov-url">URL</Label>
              <Input id="cov-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cov-date">Coverage Date</Label>
              <Input id="cov-date" type="date" value={coverageDate} onChange={(e) => setCoverageDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cov-excerpt">Excerpt (optional)</Label>
              <Textarea id="cov-excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={3} placeholder="Short blurb…" />
            </div>
            <div className="flex items-center gap-2">
              <Switch id="cov-visible" checked={isVisible} onCheckedChange={setIsVisible} />
              <Label htmlFor="cov-visible">Visible on press kit</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={() => void handleFormSave()} disabled={!titleInput.trim() || !publication.trim() || !url.trim() || formSaving}>
              {formSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {thumbnailTarget && (
        <CoverageThumbnailDialog
          coverageId={thumbnailTarget.id}
          open={thumbnailOpen}
          onOpenChange={setThumbnailOpen}
          onSuccess={(url) => { void updateThumbnail(thumbnailTarget.id, url) }}
        />
      )}
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function AdminPressKitPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Press Kit</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage public press kit content and downloadable assets.</p>
      </div>

      <Tabs defaultValue="bio">
        <TabsList>
          <TabsTrigger value="bio">Bio &amp; Stats</TabsTrigger>
          <TabsTrigger value="assets">Brand Assets</TabsTrigger>
          <TabsTrigger value="releases">Press Releases</TabsTrigger>
          <TabsTrigger value="coverage">Media Coverage</TabsTrigger>
        </TabsList>
        <TabsContent value="bio" className="mt-6">
          <div className="space-y-10">
            <BioTab />
            <div>
              <h2 className="text-base font-semibold mb-4">Statistics</h2>
              <StatsTab />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="assets" className="mt-6"><AssetsTab /></TabsContent>
        <TabsContent value="releases" className="mt-6"><ReleasesTab /></TabsContent>
        <TabsContent value="coverage" className="mt-6"><CoverageTab /></TabsContent>
      </Tabs>
    </div>
  )
}
